/**
 * Portals: a linked pair of floating ring pads (primitives with shape 'portal'
 * sharing a `portalLink`). Step (or teleport, or glide) onto one and you step
 * out beside the other, facing the way it points. Clicking / pinching a portal in the viewer
 * travels too.
 *
 * Travel is an event (PORTAL_TRAVEL_EVENT) so whoever owns the local player —
 * the desktop avatar, the view-only camera, the VR rig — moves it.
 *
 * A portal can instead lead to another world (`portalWorldId`): stepping in
 * fades out and loads /vr/<that world>?from=<this world>, and the arriving
 * page puts you beside its portal that leads back (useWorldPortalArrival).
 */
import React, { useMemo, useRef, useState } from 'react';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { create } from 'zustand';
import { useWorldStore, type PrimitiveItem } from '../../stores/worldStore';
import { travelToWorld } from '../../services/worldHop';
import './portals.css';

export const PORTAL_TRAVEL_EVENT = 'gathrxr:portal-travel';
/** Where a traveller arrives: feet position (world) and the yaw to face. */
export interface PortalArrival { position: [number, number, number]; yaw: number; /** the pad you come out of */ pad?: string }

/** Portal opening in its own (unscaled) frame: centred on the primitive's position. */
export const PORTAL_W = 1.5;   // pad diameter
export const PORTAL_H = 2.2;   // height of its volume (the mist column)
const EXIT_AHEAD = 1.35;       // step out this far in front of the exit pad (clear of its rings)
const COOLDOWN_MS = 1200;

/** The other end of a portal, if both ends exist. */
export function portalPartner(id: string, prims = useWorldStore.getState().primitives): [string, PrimitiveItem] | null {
  const me = prims[id];
  if (!me?.portalLink) return null;
  for (const [pid, p] of Object.entries(prims)) {
    if (pid !== id && p.shape === 'portal' && p.portalLink === me.portalLink && p.visible !== false) return [pid, p];
  }
  return null;
}

/** Arrival point in front of a portal (its local +Z), on the floor under it. */
export function arrivalAt(p: PrimitiveItem): PortalArrival {
  const yaw = p.rotation[1] ?? 0;
  const sy = p.scale[1] ?? 1;
  return {
    position: [
      p.position[0] + Math.sin(yaw) * EXIT_AHEAD,
      p.position[1] - (PORTAL_H / 2) * sy,
      p.position[2] + Math.cos(yaw) * EXIT_AHEAD,
    ],
    yaw,
  };
}

/** Does stepping in go anywhere: a placed exit, or another world. */
export function portalActive(id: string, prims = useWorldStore.getState().primitives): boolean {
  return !!prims[id]?.portalWorldId || !!portalPartner(id, prims);
}

/** The label over the ring: the builder's own, else where a world portal leads. */
export function portalLabelOf(p: PrimitiveItem): string | undefined {
  return p.portalLabel || (p.portalWorldId ? `To ${p.portalWorldName || 'another world'}` : undefined);
}

/** Load the world a portal leads to, if this visitor may open it (shared with the world picker). */
function travelToPortalWorld(p: PrimitiveItem) {
  if (p.portalWorldId) void travelToWorld(p.portalWorldId, p.portalWorldName);
}

let lastTravel = 0;
/**
 * Send the local player through portal `id`: to its partner, or (when
 * `worldTrips` is on) to the world it leads to. Returns false if it goes nowhere.
 */
export function travelThrough(id: string, worldTrips = true): boolean {
  const me = useWorldStore.getState().primitives[id];
  if (me?.portalWorldId) {
    if (!worldTrips) return false;
    const now = performance.now();
    if (now - lastTravel < COOLDOWN_MS) return false;
    lastTravel = now;
    travelToPortalWorld(me);
    return true;
  }
  const partner = portalPartner(id);
  if (!partner) return false;
  const now = performance.now();
  if (now - lastTravel < COOLDOWN_MS) return false;
  lastTravel = now;
  window.dispatchEvent(new CustomEvent<PortalArrival>(PORTAL_TRAVEL_EVENT, { detail: { ...arrivalAt(partner[1]), pad: partner[0] } }));
  return true;
}

// ---- Visual ----
// A floating pad of white concentric rings with an etheric mist column rising
// from it. All animation is in shaders (no per-frame CPU work beyond a clock).

const PAD_Y = -PORTAL_H / 2 + 0.14; // rings hover this far above the floor
const MIST_H = 2.0;

const PLANE_VERT = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

// Shared value noise / fbm for the mist.
const NOISE = `
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y);
  }
  float fbm(vec2 p) { float v = 0.0, a = 0.5; for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.03; a *= 0.5; } return v; }`;

/** Concentric rings: segmented arcs turning in alternate directions, a ripple pulsing outward. */
const RINGS_FRAG = `
  uniform float uTime, uActive, uHi;
  uniform vec3 uTint;
  varying vec2 vUv;
  float band(float r, float c, float w) { return 1.0 - smoothstep(0.0, w, abs(r - c)); }
  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float r = length(p);
    if (r > 1.0) discard;
    float a = atan(p.y, abs(p.x) < 1e-4 ? 1e-4 : p.x);
    float t = uTime * mix(0.15, 1.0, uActive);
    float v = 0.0;
    // four rings, each broken into arcs, spinning opposite ways
    for (int i = 0; i < 4; i++) {
      float fi = float(i);
      float c = 0.3 + fi * 0.21;
      float dir = mod(fi, 2.0) < 1.0 ? 1.0 : -1.0;
      float segs = 3.0 + fi * 2.0;
      float arcs = smoothstep(0.15, 0.3, sin(a * segs + dir * t * (0.6 - fi * 0.08) + fi) * 0.5 + 0.5);
      float w = 0.018 + 0.006 * (3.0 - fi);
      v += band(r, c, w) * mix(0.35, 1.0, arcs);
      v += band(r, c, w * 4.0) * 0.12;                 // soft halo around each ring
    }
    v += band(r, 0.12, 0.04) * 0.9;                    // bright core ring
    float ripple = fract(t * 0.35);
    v += band(r, ripple, 0.05) * (1.0 - ripple) * 0.6 * uActive; // pulse travelling out
    v += (1.0 - smoothstep(0.0, 0.9, r)) * 0.18;               // faint inner glow
    vec3 col = mix(vec3(1.0), uTint, 0.18) * (1.0 + uHi * 0.6);
    float alpha = clamp(v, 0.0, 1.0) * mix(0.35, 1.0, uActive);
    gl_FragColor = vec4(clamp(mix(vec3(0.6), col, uActive), 0.0, 1.6), alpha);
  }`;

/** Mist column: soft open cylinder of upward-drifting noise, brightest facing you, fading with height. */
const MIST_VERT = `
  varying vec2 vUv;
  varying float vFace;
  void main() {
    vUv = uv;
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vec3 n = normalize(mat3(modelMatrix) * normal);
    vec3 toCam = cameraPosition - wp.xyz;
    vFace = abs(dot(n, toCam / max(length(toCam), 1e-3)));
    gl_Position = projectionMatrix * viewMatrix * wp;
  }`;
const MIST_FRAG = NOISE + `
  uniform float uTime, uActive;
  uniform vec3 uTint;
  varying vec2 vUv;
  varying float vFace;
  void main() {
    float t = uTime;
    vec2 q = vec2(vUv.x * 6.0, vUv.y * 2.2 - t * 0.35);
    float n = fbm(q + vec2(fbm(q * 1.7 + t * 0.1), 0.0) * 0.8);
    float wisps = smoothstep(0.35, 0.85, n);
    float h = vUv.y;
    float fade = smoothstep(0.0, 0.08, h) * pow(clamp(1.0 - h, 0.0, 1.0), 1.6);
    float alpha = clamp(wisps * fade * vFace * 0.42 * uActive, 0.0, 1.0);
    gl_FragColor = vec4(mix(vec3(1.0), uTint, 0.15), alpha);
  }`;

/** Low cloud swirling over the pad. */
const CLOUD_FRAG = NOISE + `
  uniform float uTime, uActive;
  uniform vec3 uTint;
  varying vec2 vUv;
  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float r = length(p);
    if (r > 1.0) discard;
    float a = atan(p.y, abs(p.x) < 1e-4 ? 1e-4 : p.x) + uTime * 0.12 + r * 1.5;
    vec2 q = vec2(cos(a), sin(a)) * r * 2.5;
    float n = fbm(q * 1.4 + uTime * 0.08);
    float alpha = smoothstep(0.3, 0.8, n) * (1.0 - smoothstep(0.35, 1.0, r)) * 0.38 * uActive;
    gl_FragColor = vec4(mix(vec3(1.0), uTint, 0.15), alpha);
  }`;

/** Motes drifting up through the mist. */
const MOTES_VERT = `
  uniform float uTime, uRadius, uHeight;
  attribute float aSeed;
  varying float vA;
  void main() {
    float life = fract(uTime * (0.12 + aSeed * 0.1) + aSeed * 7.0);
    float ang = aSeed * 40.0 + uTime * 0.4;
    float rr = uRadius * (0.25 + 0.7 * fract(aSeed * 13.0));
    vec3 p = vec3(cos(ang) * rr, life * uHeight, sin(ang) * rr);
    vA = sin(life * 3.14159);
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    gl_PointSize = clamp(0.045 * 600.0 / max(-mv.z, 0.2), 1.0, 48.0);
    gl_Position = projectionMatrix * mv;
  }`;
const MOTES_FRAG = `
  uniform float uActive;
  varying float vA;
  void main() {
    float d = length(gl_PointCoord - 0.5);
    if (d > 0.5) discard;
    gl_FragColor = vec4(1.0, 1.0, 1.0, (1.0 - smoothstep(0.0, 0.5, d)) * max(vA, 0.0) * 0.8 * uActive);
  }`;

const MOTE_COUNT = 36;

const _cam = new THREE.Vector3();
/** Turns its children about the vertical to face the camera (drei's Y-locked
 *  Billboard flips when the camera looks steeply down). */
function FaceCamera({ position, children }: { position: [number, number, number]; children: React.ReactNode }) {
  const ref = useRef<THREE.Group>(null);
  useFrame(({ camera }) => {
    const g = ref.current;
    if (!g?.parent) return;
    camera.getWorldPosition(_cam);
    g.parent.worldToLocal(_cam);
    g.rotation.set(0, Math.atan2(_cam.x - g.position.x, _cam.z - g.position.z), 0);
  });
  return <group ref={ref} position={position}>{children}</group>;
}

/** The pad, sized to the portal (its origin is the primitive's centre, PORTAL_H/2 above the floor). */
export function PortalVisual({ color = '#8b5cf6', active = true, label, highlight = false }: {
  color?: string; active?: boolean; label?: string; highlight?: boolean;
}) {
  const R = PORTAL_W / 2;
  const mats = useMemo(() => {
    const u = () => ({ uTime: { value: 0 }, uActive: { value: 1 }, uHi: { value: 0 }, uTint: { value: new THREE.Color(color) } });
    const base = { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide } as const;
    const rings = new THREE.ShaderMaterial({ ...base, vertexShader: PLANE_VERT, fragmentShader: RINGS_FRAG, uniforms: u(), toneMapped: false });
    const cloud = new THREE.ShaderMaterial({ ...base, vertexShader: PLANE_VERT, fragmentShader: CLOUD_FRAG, uniforms: u() });
    const mist = new THREE.ShaderMaterial({ ...base, vertexShader: MIST_VERT, fragmentShader: MIST_FRAG, uniforms: u() });
    const motes = new THREE.ShaderMaterial({ ...base, vertexShader: MOTES_VERT, fragmentShader: MOTES_FRAG,
      uniforms: { ...u(), uRadius: { value: R }, uHeight: { value: MIST_H * 0.85 } } });
    return [rings, cloud, mist, motes];
  }, []);
  const motesGeom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(MOTE_COUNT * 3), 3));
    const seeds = new Float32Array(MOTE_COUNT);
    for (let i = 0; i < MOTE_COUNT; i++) seeds[i] = Math.random();
    g.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, MIST_H / 2, 0), MIST_H);
    return g;
  }, []);
  for (const m of mats) {
    m.uniforms.uTint.value.set(color);
    m.uniforms.uActive.value = active ? 1 : 0;
    m.uniforms.uHi.value = highlight ? 1 : 0;
  }
  const bob = useRef<THREE.Group>(null);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    for (const m of mats) m.uniforms.uTime.value = t;
    if (bob.current) bob.current.position.y = PAD_Y + Math.sin(t * 1.3) * 0.025;
  });
  const [rings, cloud, mist, motes] = mats;
  return <group>
    <group ref={bob} position={[0, PAD_Y, 0]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} material={rings} raycast={() => null} renderOrder={2}>
        <planeGeometry args={[R * 2, R * 2]} />
      </mesh>
      {active && <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.06, 0]} material={cloud} raycast={() => null} renderOrder={3}>
        <planeGeometry args={[R * 2.3, R * 2.3]} />
      </mesh>}
    </group>
    {active && <>
      <mesh position={[0, PAD_Y + MIST_H / 2, 0]} material={mist} raycast={() => null} renderOrder={4}>
        <cylinderGeometry args={[R * 0.7, R * 0.92, MIST_H, 32, 1, true]} />
      </mesh>
      <points position={[0, PAD_Y, 0]} geometry={motesGeom} material={motes} raycast={() => null} renderOrder={5} />
    </>}
    {label && <FaceCamera position={[0, PAD_Y + MIST_H + 0.15, 0]}>
      <Text fontSize={0.18} color="#ffffff" outlineWidth={0.012} outlineColor="#000000"
        anchorX="center" anchorY="middle" raycast={() => null}>{label}</Text>
    </FaceCamera>}
  </group>;
}

const _rc = new THREE.Raycaster();
/** Click range for travelling by clicking a portal (walking onto one has no limit). */
const CLICK_RANGE = 25;
/**
 * Is something solid between the pointer and this portal's hit box? Only
 * objects with handlers take part in pointer events, so a click on a wall (or
 * the ground, to walk) would otherwise reach a portal hidden behind it.
 */
export function clickBlocked(e: ThreeEvent<MouseEvent>, scene: THREE.Scene, own: THREE.Object3D | null): boolean {
  if (e.distance > CLICK_RANGE) return true;
  _rc.set(e.ray.origin, e.ray.direction);
  _rc.near = 0; _rc.far = Math.max(0, e.distance - 0.05);
  for (const h of _rc.intersectObjects(scene.children, true)) {
    const o = h.object as THREE.Mesh & { isSkinnedMesh?: boolean };
    if (!(o as any).isMesh || o.isSkinnedMesh) continue; // people, incl. your own avatar
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    if (mats.every((m) => !m || m.visible === false || (m.transparent && m.opacity < 0.05))) continue;
    let inOwn = false;
    for (let p: THREE.Object3D | null = o; p; p = p.parent) if (p === own) { inOwn = true; break; }
    if (inOwn) continue;
    let hidden = false;
    for (let p: THREE.Object3D | null = o; p; p = p.parent) if (!p.visible) { hidden = true; break; }
    if (!hidden) return true;
  }
  return false;
}

/** Viewer rendering: the ring, a hit box (hover glow; click / pinch to travel). */
export function ViewerPortal({ id, primitive }: { id: string; primitive: PrimitiveItem }) {
  const linked = useWorldStore((s) => portalActive(id, s.primitives));
  const [hover, setHover] = useState(false);
  const scene = useThree((s) => s.scene);
  const own = useRef<THREE.Group>(null);
  return <group ref={own} position={primitive.position} rotation={primitive.rotation} scale={primitive.scale} name={`portal-${id}`}>
    <PortalVisual color={primitive.color} active={linked} label={portalLabelOf(primitive)} highlight={hover} />
    <mesh userData={{ portal: id }}
      onPointerOver={(e) => { e.stopPropagation(); setHover(!clickBlocked(e as unknown as ThreeEvent<MouseEvent>, scene, own.current)); }}
      onPointerOut={() => setHover(false)}
      onClick={(e) => {
        // A click that really landed on something in front of the portal is
        // that thing's click (or a walk-to), not a trip.
        if (clickBlocked(e, scene, own.current)) return;
        e.stopPropagation();
        travelThrough(id);
      }}>
      <boxGeometry args={[PORTAL_W, PORTAL_H, PORTAL_W]} />
      <meshBasicMaterial visible={false} />
    </mesh>
  </group>;
}

// ---- Walking through ----

const _inv = new THREE.Matrix4();
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _feet = new THREE.Vector3();
const ONE = new THREE.Vector3(1, 1, 1);

/**
 * Watches the local player and sends them through any portal they cross.
 * `avatarRef`: the desktop avatar group (feet) or, in first-person mode, the
 * camera (eyes). In VR the headset is used. `worldTrips` off (the editor):
 * portals to other worlds don't fire, so building near one can't whisk you away.
 */
export function PortalTraveler({ avatarRef, worldTrips = true }: { avatarRef?: React.MutableRefObject<THREE.Object3D | null>; worldTrips?: boolean }) {
  const { camera } = useThree();
  const presenting = useXR((s) => !!s.session);
  const prev = useRef<THREE.Vector3 | null>(null);
  // Pads that don't fire until you've walked away from them: the one you just
  // came out of (so settling or a nudge after arriving can't bounce you back).
  const disarmed = useRef(new Set<string>());
  // No trips while the world is still placing you (spawn, first frames).
  const readyAt = useRef(0);
  useFrame(() => {
    const prims = useWorldStore.getState().primitives;
    if (!readyAt.current) readyAt.current = performance.now() + 2000;
    let any = false;
    for (const id in prims) if (prims[id].shape === 'portal') { any = true; break; }
    if (!any) { prev.current = null; return; }
    // Player's feet in world space.
    const src = presenting ? null : avatarRef?.current;
    if (src) {
      src.getWorldPosition(_feet);
      if ((src as any).isCamera) _feet.y -= 1.6;
    } else {
      camera.getWorldPosition(_feet);
      _feet.y -= presenting ? 1.5 : 1.6;
    }
    if (!prev.current) { prev.current = _feet.clone(); return; }
    if (prev.current.distanceToSquared(_feet) < 1e-6) return;
    // A long move this frame is a teleport: only where it lands counts (not
    // pads it flew over).
    const jump = prev.current.distanceToSquared(_feet) > 9;
    for (const id in prims) {
      const p = prims[id];
      if (p.shape !== 'portal' || p.visible === false) continue;
      // Pad frame without scale; its radius scales with it.
      _e.set(p.rotation[0], p.rotation[1], p.rotation[2]);
      _m.compose(_a.set(p.position[0], p.position[1], p.position[2]), _q.setFromEuler(_e), ONE);
      _inv.copy(_m).invert();
      _a.copy(prev.current).applyMatrix4(_inv);
      _b.copy(_feet).applyMatrix4(_inv);
      const r = (PORTAL_W / 2) * 0.85 * Math.min(p.scale[0] ?? 1, p.scale[2] ?? 1);
      if (disarmed.current.has(id)) {
        if (Math.hypot(_b.x, _b.z) > r + 1.6) disarmed.current.delete(id);
        continue;
      }
      if (performance.now() < readyAt.current) continue;
      const floor = -(PORTAL_H / 2) * (p.scale[1] ?? 1);
      // Feet at the pad's level (not walking under / over it on another floor)…
      if (_b.y < floor - 0.6 || _b.y > floor + 1.0) continue;
      // …stepping onto it this frame: was outside, and the move reaches it.
      if (Math.hypot(_a.x, _a.z) <= r) continue;
      let d: number;
      const sx = _b.x - _a.x, sz = _b.z - _a.z;
      const len2 = sx * sx + sz * sz;
      // A teleport counts only where it lands; so does a purely up/down move
      // (landing, settling onto the floor), which has no sideways path. Without
      // this the closest-point maths divided by zero and NaN read as "on the pad".
      if (jump || len2 < 1e-9) d = Math.hypot(_b.x, _b.z);
      else {
        const t = Math.max(0, Math.min(1, -(_a.x * sx + _a.z * sz) / len2));
        d = Math.hypot(_a.x + sx * t, _a.z + sz * t);
      }
      if (!(d <= r)) continue;
      if (travelThrough(id, worldTrips)) break;
    }
    // (A trip clears prev — tracking restarts from the arrival point.)
    if (prev.current) prev.current.copy(_feet);
  });
  // After a jump, start tracking from the arrival point (not a line back to the old spot).
  React.useEffect(() => {
    const onTravel = (e: Event) => {
      prev.current = null;
      const pad = (e as CustomEvent<PortalArrival>).detail?.pad;
      if (pad) disarmed.current.add(pad);
    };
    window.addEventListener(PORTAL_TRAVEL_EVENT, onTravel);
    return () => window.removeEventListener(PORTAL_TRAVEL_EVENT, onTravel);
  }, []);
  return null;
}

/** Subscribe to portal travel (the local player's controller moves itself). */
export function usePortalArrival(handler: (a: PortalArrival) => void) {
  const ref = useRef(handler);
  ref.current = handler;
  React.useEffect(() => {
    const on = (e: Event) => ref.current((e as CustomEvent<PortalArrival>).detail);
    window.addEventListener(PORTAL_TRAVEL_EVENT, on);
    return () => window.removeEventListener(PORTAL_TRAVEL_EVENT, on);
  }, []);
}

/**
 * Arriving from another world's portal (?from=<world id>): once this world is
 * ready, step out beside the portal here that leads back there. Without one
 * you simply start at the spawn point.
 */
export function useWorldPortalArrival(ready: boolean) {
  React.useEffect(() => {
    if (!ready) return;
    const url = new URL(window.location.href);
    const from = url.searchParams.get('from');
    if (!from) return;
    url.searchParams.delete('from');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    // Give the player controller a moment to mount and take its spawn first.
    const t = setTimeout(() => {
      const prims = useWorldStore.getState().primitives;
      const back = Object.entries(prims).find(([, p]) => p.shape === 'portal' && p.portalWorldId === from && p.visible !== false);
      if (!back) return;
      window.dispatchEvent(new CustomEvent<PortalArrival>(PORTAL_TRAVEL_EVENT, { detail: { ...arrivalAt(back[1]), pad: back[0] } }));
    }, 1500);
    return () => clearTimeout(t);
  }, [ready]);
}

// ---- Builder: placing a pair ----
/** The entrance placed so far while the Portal tool waits for the exit click. */
export const usePortalDraft = create<{ pending: { link: string; id: string } | null; set: (p: { link: string; id: string } | null) => void }>((set) => ({
  pending: null, set: (pending) => set({ pending }),
}));
export const newPortalLink = () => `portal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

/** A portal still waiting for its exit: the selected one if it qualifies, else the newest. */
export function portalNeedingExit(): string | null {
  const s = useWorldStore.getState();
  const unlinked = (id: string) => s.primitives[id]?.shape === 'portal' && !!s.primitives[id].portalLink && !portalPartner(id, s.primitives);
  if (s.selectedObjectId && unlinked(s.selectedObjectId)) return s.selectedObjectId;
  const ids = Object.keys(s.primitives).filter(unlinked);
  return ids.length ? ids[ids.length - 1] : null;
}
/** Arm the builder to place the exit of portal `id` (one click, then the tool switches off). */
export function startPlacePortalExit(id: string) {
  const s = useWorldStore.getState();
  const link = s.primitives[id]?.portalLink;
  if (!link) return;
  s.setPlacementSpec({ kind: 'primitive', shape: 'portal', portalExitFor: link });
  s.setPlacementRotY(0);
  s.setBuildTool('place');
}
